import { useState, useEffect, useCallback } from 'react';
import {
  Crown,
  CreditCard,
  AlertCircle,
  Loader2,
  ExternalLink,
  Users,
  Calendar,
  XCircle,
  Gift,
  RotateCcw,
  Smartphone,
  Globe,
  CheckCircle,
  Tag,
} from 'lucide-react';
import {
  useSubscription,
  useSubscriptionDetails,
  useCreateCheckout,
  useCreatePortalSession,
  formatPrice,
} from '../hooks/useSubscription';
import { usePrices } from '../hooks/usePrices';
import { usePurchaseIAP, useRestorePurchases, REVENUECAT_PRODUCTS } from '../hooks/useRevenueCat';
import { usePremiumContent } from '../hooks/usePremiumContent';
import { isNative, isIOS } from '@/lib/capacitor';
import { STRIPE_PRICES } from '@/lib/stripe';
import { Check, X } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

export default function SubscriptionDetailsPage() {
  const { t, i18n } = useTranslation('subscription');
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const {
    isPremium,
    isBetaTester,
    betaActivatedAt,
    isSharedPremium,
    premiumExpiresAt,
    premiumSource,
    isLoading: basicLoading,
  } = useSubscription();

  // Check if user has promo code access
  const isPromoUser = premiumSource === 'promo_code';
  const isAppleUser = premiumSource === 'apple';
  const isGoogleUser = premiumSource === 'google';
  const isIAPUser = isAppleUser || isGoogleUser;

  const { data: details, isLoading: detailsLoading } = useSubscriptionDetails();
  const prices = usePrices();
  const { features } = usePremiumContent();
  const createCheckout = useCreateCheckout();
  const createPortal = useCreatePortalSession();
  const purchaseIAP = usePurchaseIAP();
  const restorePurchases = useRestorePurchases();

  // Render feature value
  const renderFeatureValue = (value: boolean | string, isPremiumValue: boolean) => {
    if (typeof value === 'string') {
      return <span className={isPremiumValue ? 'text-pp-orange font-medium' : 'text-white/60'}>{value}</span>;
    }
    return value ? (
      <Check className={`w-4 h-4 ${isPremiumValue ? 'text-pp-orange' : 'text-green-400'}`} />
    ) : (
      <X className="w-4 h-4 text-white/30" />
    );
  };

  const queryClient = useQueryClient();

  // Promo code state
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoRedeeming, setPromoRedeeming] = useState(false);
  const [promoValidation, setPromoValidation] = useState<{
    valid: boolean;
    error?: string;
    code_type?: 'beta' | 'promo';
    promo_months?: number | null;
    is_lifetime?: boolean;
    remaining_uses?: number | null;
  } | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState(false);

  const validatePromoCode = useCallback(async (inputCode: string) => {
    const trimmedCode = inputCode.trim().toUpperCase();
    if (!trimmedCode || trimmedCode.length < 4) {
      setPromoValidation(null);
      setPromoError(null);
      return;
    }
    setPromoValidating(true);
    setPromoError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('validate_promo_code', {
        p_code: trimmedCode,
      });
      if (rpcError) {
        setPromoError(t('promo.validationError', { defaultValue: 'Validierungsfehler' }));
        setPromoValidation(null);
        return;
      }
      setPromoValidation(data);
      if (!data.valid) {
        setPromoError(data.error || t('promo.invalidCode', { defaultValue: 'Ungültiger Code' }));
      }
    } catch {
      setPromoError(t('promo.validationError', { defaultValue: 'Validierungsfehler' }));
      setPromoValidation(null);
    } finally {
      setPromoValidating(false);
    }
  }, [t]);

  const handlePromoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value.toUpperCase();
    setPromoCode(newCode);
    setPromoValidation(null);
    setPromoError(null);
  };

  const handlePromoValidate = () => {
    validatePromoCode(promoCode);
  };

  const handlePromoRedeem = async () => {
    if (!promoValidation?.valid) return;
    setPromoRedeeming(true);
    setPromoError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPromoError(t('promo.errors.invalidCode'));
        return;
      }
      const { data: result, error: rpcError } = await supabase.rpc('redeem_promo_code', {
        p_user_id: user.id,
        p_code: promoCode.trim().toUpperCase(),
      });
      if (rpcError) {
        // Postgres unique_violation (parallel double-click) → Lifetime-Cap
        if ((rpcError as { code?: string }).code === '23505') {
          setPromoError(t('promo.errors.lifetimeCap'));
        } else {
          setPromoError(t('promo.errors.invalidCode'));
        }
        return;
      }
      if (result && typeof result === 'object' && 'success' in result && !result.success) {
        const errorCode = (result as { error_code?: string }).error_code;
        switch (errorCode) {
          case 'lifetime_cap':
            setPromoError(t('promo.errors.lifetimeCap'));
            break;
          case 'already_premium':
            setPromoError(t('promo.errors.alreadyPremium'));
            break;
          case 'invalid_code':
            setPromoError(t('promo.errors.invalidCode'));
            break;
          default:
            setPromoError(t('promo.errors.invalidCode'));
        }
        return;
      }
      setPromoSuccess(true);
      // Refresh subscription state
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-details'] });
    } catch {
      setPromoError(t('promo.errors.invalidCode'));
    } finally {
      setPromoRedeeming(false);
    }
  };

  const getPromoDurationText = (): string => {
    if (!promoValidation?.valid) return '';
    if (promoValidation.is_lifetime || promoValidation.code_type === 'beta') {
      return t('promo.lifetime', { defaultValue: 'Lebenslang' });
    }
    const months = promoValidation.promo_months;
    if (!months) return 'Premium-Zugang';
    if (months === 1) return '1 Monat Premium gratis';
    if (months === 12) return '1 Jahr Premium gratis';
    return `${months} Monate Premium gratis`;
  };

  const promoIsValid = promoValidation?.valid === true;

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'sixMonth' | 'yearly'>('sixMonth');
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [errorTimeLeft, setErrorTimeLeft] = useState(0);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (purchaseError) {
      setErrorTimeLeft(5);
      const interval = setInterval(() => {
        setErrorTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setPurchaseError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [purchaseError]);

  // Clear error when changing plan
  const handlePlanChange = (plan: 'monthly' | 'sixMonth' | 'yearly') => {
    setPurchaseError(null);
    setSelectedPlan(plan);
  };

  const isLoading = basicLoading || detailsLoading || prices.isLoading;
  const isPurchasing = createCheckout.isPending || purchaseIAP.isPending;

  // Handle subscribe - uses Stripe on web, IAP on native
  const handleSubscribe = async () => {
    setPurchaseError(null);

    if (isNative) {
      // Native: Use RevenueCat IAP
      try {
        const productId = selectedPlan === 'monthly'
          ? REVENUECAT_PRODUCTS.MONTHLY
          : selectedPlan === 'sixMonth'
            ? REVENUECAT_PRODUCTS.SIX_MONTH
            : REVENUECAT_PRODUCTS.YEARLY;

        await purchaseIAP.mutateAsync(productId);
        // Success is handled by query invalidation
      } catch (error) {
        if (error instanceof Error && error.message === 'PURCHASE_CANCELLED') {
          // User cancelled - no error message needed
          return;
        }
        // Log detailed error info for debugging
        console.error('IAP Error:', error instanceof Error ? error.message : 'Unknown error');
        if (error && typeof error === 'object') {
          console.error('IAP Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
        }
        setPurchaseError(t('error.purchaseFailed'));
      }
    } else {
      // Web: Use Stripe
      try {
        const priceId = selectedPlan === 'monthly'
          ? STRIPE_PRICES.PREMIUM_MONTHLY
          : selectedPlan === 'sixMonth'
            ? STRIPE_PRICES.PREMIUM_6MONTH
            : STRIPE_PRICES.PREMIUM_1YEAR;

        const result = await createCheckout.mutateAsync({
          priceId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription`,
        });

        if (result.url) {
          window.location.href = result.url;
        }
      } catch (error) {
        console.error('Checkout Error:', error);
        setPurchaseError(t('error.checkoutFailed'));
      }
    }
  };

  // Handle restore purchases (native only)
  const handleRestorePurchases = async () => {
    setPurchaseError(null);
    try {
      const result = await restorePurchases.mutateAsync();
      if (!result.hasPremium) {
        setPurchaseError(t('error.noRestorePurchases'));
      }
    } catch (error) {
      console.error('Restore Error:', error);
      setPurchaseError(t('error.restoreFailed'));
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await createPortal.mutateAsync(
        `${window.location.origin}/subscription`
      );

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <h1 className="text-2xl font-bold text-white">{t('title')}</h1>

      {/* Current Status Card */}
      <div className="glass-effect rounded-[15px] p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-[5px] glass-effect flex items-center justify-center">
            <img
              src="https://parentpath.de/pp_logo-piktogram-w100.svg"
              alt="ParentPath"
              className="h-8 w-auto"
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className={`font-semibold text-lg ${isPremium && !isBetaTester && !isPromoUser ? 'text-pp-orange' : 'text-white'}`}>
                {isPremium ? t('plan.premium') : t('plan.free')}
              </h2>
              {isBetaTester && (
                <span className="px-2 py-0.5 bg-purple-500 text-white text-xs font-medium rounded-full">
                  {t('beta.label')}
                </span>
              )}
              {isPromoUser && (
                <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                  <Gift className="w-3 h-3" />
                  {t('promo.label')}
                </span>
              )}
              {isIAPUser && (
                <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                  <Smartphone className="w-3 h-3" />
                  {isAppleUser ? 'App Store' : 'Google Play'}
                </span>
              )}
              {isSharedPremium && (
                <span className="px-2 py-0.5 bg-pp-orange text-white text-xs font-medium rounded-full flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {t('shared.label')}
                </span>
              )}
            </div>
            {details?.status && details.status !== 'none' && (
              <span
                className={`text-sm ${
                  details.status === 'active'
                    ? 'text-green-400'
                    : details.status === 'past_due'
                      ? 'text-amber-400'
                      : 'text-white/50'
                }`}
              >
                {t(`status.${details.status}`)}
              </span>
            )}
          </div>
        </div>

        {/* Details for Premium users */}
        {isPremium && (
          <div className="space-y-3 border-t border-white/15 pt-4">
            {/* Beta Info */}
            {isBetaTester && (
              <>
                <p className="text-purple-400 text-sm mb-4">
                  {t('beta.description')}
                </p>

                {/* Beta Activation Date */}
                {betaActivatedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('beta.accessSince')}</span>
                    <span className="font-medium text-white flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(betaActivatedAt)}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Promo Code Info */}
            {isPromoUser && (
              <>
                <p className="text-amber-400 text-sm mb-4">
                  {t('promo.description')}
                </p>

                {/* Promo Expiration Date */}
                {premiumExpiresAt ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('promo.validUntil')}</span>
                    <span className="font-medium text-white flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(premiumExpiresAt)}
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('promo.access')}</span>
                    <span className="font-medium text-green-400">{t('promo.lifetime')}</span>
                  </div>
                )}

                {/* Expiration Warning */}
                {premiumExpiresAt && new Date(premiumExpiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                  <div className="bg-amber-500/20 border border-amber-500/30 rounded-[15px] p-3 mt-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-300">{t('promo.expiringSoon')}</p>
                        <p className="text-sm text-amber-200/70">
                          {t('promo.expiringDescription', { date: formatDate(premiumExpiresAt) })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* IAP Info */}
            {isIAPUser && (
              <div className="bg-blue-500/20 border border-blue-500/30 rounded-[15px] p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Smartphone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-300">
                      {isAppleUser ? t('iap.appStoreSubscription') : t('iap.googlePlaySubscription')}
                    </p>
                    <p className="text-sm text-white/70">
                      {t('iap.manageInSettings', { platform: isAppleUser ? t('iap.iphoneSettings') : t('iap.googlePlay') })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Shared Premium Info */}
            {isSharedPremium && (
              <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-[15px] p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Users className="w-5 h-5 text-pp-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-pp-orange">{t('shared.title')}</p>
                    <p className="text-sm text-white/70">
                      {t('shared.description')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Plan - only show if we have Stripe details */}
            {details?.plan && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">{t('plan.currentPlan')}</span>
                  <span className="font-medium text-white">
                    {isBetaTester ? t('beta.premiumBeta') : details.plan.name} ({details.plan.interval === 'month' ? t('pricing.monthly') : t('pricing.yearly')})
                  </span>
                </div>

                {/* Price */}
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">{t('pricing.price')}</span>
                  <span className="font-medium text-white">
                    {isBetaTester ? (
                      <span className="text-purple-400">{t('beta.freeBeta')}</span>
                    ) : (
                      `${formatPrice(details.plan.amount, details.plan.currency)} / ${details.plan.interval === 'month' ? t('pricing.interval.month') : t('pricing.interval.year')}`
                    )}
                  </span>
                </div>

                {/* Period */}
                {details.currentPeriodStart && details.currentPeriodEnd && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('pricing.currentPeriod')}</span>
                    <span className="font-medium text-white">
                      {formatDate(details.currentPeriodStart)} - {formatDate(details.currentPeriodEnd)}
                    </span>
                  </div>
                )}

                {/* Payment Method */}
                {details.paymentMethod && !isBetaTester && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('payment.method')}</span>
                    <span className="font-medium text-white flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />
                      {details.paymentMethod.type === 'card'
                        ? `${details.paymentMethod.brand?.toUpperCase()} ****${details.paymentMethod.last4}`
                        : `SEPA ****${details.paymentMethod.last4}`}
                    </span>
                  </div>
                )}

                {/* Cancellation notice */}
                {details.cancelAtPeriodEnd && (
                  <div className="bg-amber-500/20 border border-amber-500/30 rounded-[15px] p-3 mt-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-300">{t('cancellation.title')}</p>
                        <p className="text-sm text-amber-200/70">
                          {t('cancellation.description', { date: formatDate(details.currentPeriodEnd!) })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              </>
            )}

            {/* Manage & Cancel Buttons - only for Stripe subscribers (not IAP) */}
            {!isBetaTester && !isPromoUser && !isSharedPremium && !isIAPUser && premiumSource === 'stripe' && (
              <div className="mt-4 space-y-3">
                {/* Abo verwalten */}
                <div>
                  <button
                    onClick={handleManageSubscription}
                    disabled={createPortal.isPending}
                    className="w-full py-3 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {createPortal.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4" />
                    )}
                    {t('actions.manageSubscription')}
                  </button>
                  <p className="text-xs text-white/50 text-center mt-1">
                    {t('manage.invoicesAndPayment')}
                  </p>
                </div>

                {/* Abo kündigen */}
                <div>
                  <button
                    onClick={handleManageSubscription}
                    disabled={createPortal.isPending}
                    className="w-full py-3 glass-effect border border-red-500/30 text-red-400 font-medium rounded-[15px] hover:bg-red-500/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    {t('actions.cancelSubscription')}
                  </button>
                  <p className="text-xs text-white/50 text-center mt-1">
                    {t('manage.cancelAnytime')}
                  </p>
                </div>
              </div>
            )}

            {/* Info for non-Stripe premium users */}
            {(isBetaTester || isPromoUser || isSharedPremium) && (
              <div className="mt-4 text-center text-sm text-white/50">
                {isBetaTester && t('beta.managedAutomatically')}
                {isPromoUser && t('promo.managedAutomatically')}
                {isSharedPremium && t('shared.managedByPartner')}
              </div>
            )}

            {/* Premium Features - Dynamic from Admin Panel */}
            <div className="mt-4 pt-4 border-t border-white/15">
              <h3 className="font-medium mb-3 text-sm text-white/70">{t('premiumBenefits')}</h3>
              <div className="space-y-3">
                {features.map((feature) => (
                  <div key={feature.id} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-pp-orange" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/90">{feature.name}</div>
                      {typeof feature.premium === 'string' && (
                        <div className="text-xs text-pp-orange/80">{feature.premium}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Upgrade CTA for Free users */}
        {!isPremium && (
          <div className="border-t border-white/15 pt-4">
            <p className="text-white/70 text-sm mb-4">
              {t('upgradeCta')}
            </p>
          </div>
        )}
      </div>

      {/* Pricing Cards for Free users */}
      {!isPremium && (
        <>
          <h2 className="font-semibold text-white mt-6">{t('actions.choosePlan')}</h2>

          {/* Plan Selection - 3 Options */}
          <div className="grid grid-cols-3 gap-2">
            {/* Monthly */}
            <button
              onClick={() => handlePlanChange('monthly')}
              className={`p-3 rounded-[15px] border-2 text-center transition-all ${
                selectedPlan === 'monthly'
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              }`}
            >
              <div className="text-xs text-white/50 mb-1">{t('pricing.monthly')}</div>
              <div className="text-lg font-bold text-white">
                {prices.monthly?.priceFormatted || '3,99 €'}
              </div>
              <div className="text-xs text-white/50">{t('pricing.perMonthShort')}</div>
            </button>

            {/* 6-Month */}
            <button
              onClick={() => handlePlanChange('sixMonth')}
              className={`p-3 rounded-[15px] border-2 text-center transition-all relative ${
                selectedPlan === 'sixMonth'
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              }`}
            >
              {prices.sixMonth?.savingsPercent && prices.sixMonth.savingsPercent > 0 && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                  -{prices.sixMonth.savingsPercent}%
                </div>
              )}
              <div className="text-xs text-white/50 mb-1">{t('pricing.sixMonth')}</div>
              <div className="text-lg font-bold text-white">
                {prices.sixMonth?.priceFormatted || '14,99 €'}
              </div>
              <div className="text-xs text-green-400">{t('pricing.freeTrial')}</div>
            </button>

            {/* 1 Jahr */}
            <button
              onClick={() => handlePlanChange('yearly')}
              className={`p-3 rounded-[15px] border-2 text-center transition-all relative ${
                selectedPlan === 'yearly'
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              }`}
            >
              {prices.yearly?.savingsPercent && prices.yearly.savingsPercent > 0 && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                  -{prices.yearly.savingsPercent}%
                </div>
              )}
              <div className="text-xs text-white/50 mb-1">{t('pricing.oneYear')}</div>
              <div className="text-lg font-bold text-white">
                {prices.yearly?.priceFormatted || '23,99 €'}
              </div>
              <div className="text-xs text-green-400">{t('pricing.freeTrial')}</div>
            </button>
          </div>

          {/* Selected Plan Info */}
          <div className="text-center text-sm text-white/60 mt-2">
            {selectedPlan === 'monthly' && t('pricing.flexibleCancel')}
            {selectedPlan === 'sixMonth' && t('pricing.freeTrialThen', { price: prices.sixMonth?.perMonthFormatted || '2,50 €' })}
            {selectedPlan === 'yearly' && t('pricing.freeTrialThen', { price: prices.yearly?.perMonthFormatted || '2,00 €' })}
          </div>

          {/* Premium Benefits - Dynamic 2-Column Layout */}
          <div className="glass-effect rounded-[15px] p-4">
            <h3 className="font-medium text-white mb-3">{t('benefitsWithPremium')}</h3>
            {/* Table Header */}
            <div className="grid grid-cols-2 gap-2 px-3 py-2 mb-2 bg-white/5 rounded-[10px]">
              <div className="text-xs font-medium text-white/50 text-center">{t('comparison.free')}</div>
              <div className="text-xs font-medium text-pp-orange text-center">{t('comparison.premium')}</div>
            </div>
            {/* Feature Rows */}
            <div className="space-y-3">
              {features.map((feature) => (
                <div key={feature.id} className="border-b border-white/10 pb-3 last:border-0 last:pb-0">
                  {/* Feature Name centered */}
                  <div className="text-sm font-medium text-white/90 mb-2 text-center">{feature.name}</div>
                  {/* Values */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-center">
                      {renderFeatureValue(feature.free, false)}
                    </div>
                    <div className="flex justify-center">
                      {renderFeatureValue(feature.premium, true)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Error message */}
          {purchaseError && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-[15px] overflow-hidden">
              <div className="p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-300">{purchaseError}</p>
                  </div>
                  <button
                    onClick={() => setPurchaseError(null)}
                    className="text-red-400/70 hover:text-red-300 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              <div className="h-1 bg-red-500/20">
                <div
                  className="h-full bg-red-400 transition-all duration-1000 ease-linear"
                  style={{ width: `${(errorTimeLeft / 5) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Web Fallback Notice (shown on native when IAP is disabled/unavailable) */}
          {isNative && prices.isWebFallback && (
            <div className="bg-blue-500/20 border border-blue-500/30 rounded-[15px] p-4">
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-300">{t('webFallback.title')}</p>
                  <p className="text-sm text-white/70 mt-1">
                    {t('webFallback.description')}
                  </p>
                  <p className="text-sm text-white/90 mt-2 font-mono">
                    app.parentpath.de/subscription
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Promo Code Section */}
          <div className="glass-effect rounded-[15px] overflow-hidden">
            {promoSuccess ? (
              <div className="p-4 flex items-center gap-3 text-green-400">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{t('promoRedeemed.success', { defaultValue: 'Promo-Code eingelöst!' })}</p>
                  <p className="text-sm text-white/60">{t('promoRedeemed.enjoy', { defaultValue: 'Du hast jetzt Premium-Zugang.' })}</p>
                </div>
              </div>
            ) : !showPromoInput ? (
              <button
                onClick={() => setShowPromoInput(true)}
                className="w-full p-4 flex items-center justify-center gap-2 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
              >
                <Tag className="w-4 h-4" />
                <span className="text-sm font-medium">{t('promoInput.button', { defaultValue: 'Promo-Code einlösen' })}</span>
              </button>
            ) : (
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white/70">{t('promoInput.title', { defaultValue: 'Promo-Code' })}</span>
                  <button
                    onClick={() => {
                      setShowPromoInput(false);
                      setPromoCode('');
                      setPromoValidation(null);
                      setPromoError(null);
                    }}
                    className="text-white/40 hover:text-white/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={handlePromoInputChange}
                      placeholder={t('promoInput.placeholder', { defaultValue: 'Code eingeben...' })}
                      className={cn(
                        'w-full px-4 py-3 text-center font-mono tracking-[0.15em] rounded-[10px] outline-none transition-colors uppercase text-sm',
                        promoIsValid
                          ? 'border-2 border-green-500 bg-green-500/10 text-white'
                          : promoError
                            ? 'border-2 border-red-400 bg-red-500/10 text-white'
                            : 'border-2 border-white/20 bg-white/10 text-white placeholder-white/30 focus:border-pp-orange'
                      )}
                    />
                    {promoValidating && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 animate-spin text-white/50" />
                      </div>
                    )}
                    {promoIsValid && !promoValidating && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      </div>
                    )}
                  </div>
                  {!promoIsValid ? (
                    <button
                      onClick={handlePromoValidate}
                      disabled={promoValidating || promoCode.trim().length < 4}
                      className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-[10px] text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      {t('promoInput.check', { defaultValue: 'Prüfen' })}
                    </button>
                  ) : (
                    <button
                      onClick={handlePromoRedeem}
                      disabled={promoRedeeming}
                      className="px-4 py-3 bg-pp-orange hover:bg-pp-orange/90 text-white rounded-[10px] text-sm font-medium disabled:opacity-50 transition-colors flex items-center gap-1"
                    >
                      {promoRedeeming ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Gift className="w-4 h-4" />
                      )}
                      {t('promoInput.redeem', { defaultValue: 'Einlösen' })}
                    </button>
                  )}
                </div>
                {/* Validation success */}
                {promoIsValid && (
                  <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>{getPromoDurationText()}</span>
                  </div>
                )}
                {/* Error */}
                {promoError && !promoValidating && (
                  <div className="flex items-center justify-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{promoError}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subscribe Button - only show when NOT in web fallback mode on native */}
          {!(isNative && prices.isWebFallback) && (
            <button
              onClick={handleSubscribe}
              disabled={isPurchasing}
              className="w-full py-4 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isPurchasing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Crown className="w-5 h-5" />
              )}
              {t('actions.subscribNow')}
            </button>
          )}

          {/* Restore Purchases Button (native only, not in fallback mode) */}
          {isNative && !prices.isWebFallback && (
            <button
              onClick={handleRestorePurchases}
              disabled={restorePurchases.isPending}
              className="w-full py-3 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {restorePurchases.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              {t('iap.restorePurchases')}
            </button>
          )}

          {/* Payment info - only show when NOT in fallback mode */}
          {!(isNative && prices.isWebFallback) && (
            <div className="text-xs text-white/50 text-center space-y-2">
              <p>
                {t('paymentInfo.securePaymentVia', { platform: isNative ? (isIOS ? 'Apple' : 'Google Play') : 'Stripe' })}
              </p>
              <p>
                {t('paymentInfo.acceptTerms')}{' '}
                <a href="/terms" className="text-pp-orange hover:underline">
                  {t('paymentInfo.termsOfService')}
                </a>{' '}
                {t('paymentInfo.and')}{' '}
                <a href="/privacy" className="text-pp-orange hover:underline">
                  {t('paymentInfo.privacyPolicy')}
                </a>
                .
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
